import React, { useState } from 'react';
import { GradientTitle, AccentButton, OutlineButton } from '../../ui';
import { Portal } from '../../ui/Portal';
import planeOutline from '../../../assets/survivorship/plane_outline.png';
import returnedPlane from '../../../assets/survivorship/returned.png';
import notReturnedPlane from '../../../assets/survivorship/not_returned.png';
import { Target, ShieldCheck, AlertTriangle, ArrowLeftRight, X, Brain, Microscope, BookOpen } from 'lucide-react';
import { ResultBlock } from '../SimulationTemplate';
import { Text } from '../../ui/typography/Text';

// Define plane sections for more accurate hit placement
const PLANE_SECTIONS = {
  leftWingOuter: {
    x: { min: 15, max: 35 },
    y: { min: 42, max: 48 },
    density: 0.5  // Increased density for more hits
  },
  leftWingInner: {
    x: { min: 35, max: 42 },
    y: { min: 42, max: 48 },
    density: 0.4
  },
  rightWingInner: {
    x: { min: 58, max: 65 },
    y: { min: 42, max: 48 },
    density: 0.4
  },
  rightWingOuter: {
    x: { min: 65, max: 85 },
    y: { min: 42, max: 48 },
    density: 0.5  // Increased density for more hits
  },
  tailVerticalLeft: {
    x: { min: 44, max: 47 },
    y: { min: 65, max: 75 },  // Extended tail height
    density: 0.2
  },
  tailVerticalRight: {
    x: { min: 53, max: 56 },
    y: { min: 65, max: 75 },  // Extended tail height
    density: 0.2
  },
  tailHorizontalLeft: {
    x: { min: 35, max: 47 },
    y: { min: 65, max: 68 },  // Adjusted position
    density: 0.2
  },
  tailHorizontalRight: {
    x: { min: 53, max: 65 },
    y: { min: 65, max: 68 },  // Adjusted position
    density: 0.2
  },
  leftEngine: {
    x: { min: 35, max: 40 },
    y: { min: 42, max: 48 },
    density: 0.1
  },
  rightEngine: {
    x: { min: 60, max: 65 },
    y: { min: 42, max: 48 },
    density: 0.1
  },
  cockpit: {
    x: { min: 47, max: 53 },
    y: { min: 15, max: 30 },
    density: 0.1
  }
};

// Helper function to generate scattered points within a section
const generateScatteredPoints = (section: typeof PLANE_SECTIONS.leftWingOuter, count: number) => {
  const points: Array<{ x: number; y: number }> = [];
  const adjustedCount = Math.floor(count * section.density);

  for (let i = 0; i < adjustedCount; i++) {
    // Use gaussian distribution for more natural clustering
    const randX = () => {
      const u1 = Math.random();
      const u2 = Math.random();
      const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
      return z * 0.12 + 0.5; // Reduced spread for even tighter clustering
    };

    const x = section.x.min + (section.x.max - section.x.min) * randX();
    const y = section.y.min + (section.y.max - section.y.min) * randX();

    // Additional check to avoid central fuselage area
    const isFuselageArea = x >= 45 && x <= 55 && y >= 35 && y <= 65;
    const isCabinArea = x >= 47 && x <= 53 && y >= 15 && y <= 35;
    
    // Ensure point is within bounds and not in restricted areas
    if (x >= section.x.min && x <= section.x.max && 
        y >= section.y.min && y <= section.y.max && 
        !isFuselageArea && !isCabinArea) {
      points.push({ x, y });
    }
  }
  return points;
};

// Generate hits for returning aircraft (adjusted counts)
const RETURNING_HITS = [
  ...generateScatteredPoints(PLANE_SECTIONS.leftWingOuter, 90),  // Increased hits
  ...generateScatteredPoints(PLANE_SECTIONS.leftWingInner, 60),
  ...generateScatteredPoints(PLANE_SECTIONS.rightWingInner, 60),
  ...generateScatteredPoints(PLANE_SECTIONS.rightWingOuter, 90),  // Increased hits
  ...generateScatteredPoints(PLANE_SECTIONS.tailVerticalLeft, 15),
  ...generateScatteredPoints(PLANE_SECTIONS.tailVerticalRight, 15),
  ...generateScatteredPoints(PLANE_SECTIONS.tailHorizontalLeft, 15),
  ...generateScatteredPoints(PLANE_SECTIONS.tailHorizontalRight, 15)
];

// Generate hits for lost aircraft (focused on critical areas)
const LOST_HITS = [
  ...generateScatteredPoints(PLANE_SECTIONS.leftEngine, 30),
  ...generateScatteredPoints(PLANE_SECTIONS.rightEngine, 30),
  ...generateScatteredPoints(PLANE_SECTIONS.cockpit, 35)
];

interface DamageArea {
  id: string;
  name: string;
  position: {
    left: string;
    top: string;
    width: string;
    height: string;
  };
  stats: {
    returned: string;
    interpretation: string;
  };
}

const DAMAGE_AREAS: DamageArea[] = [
  {
    id: 'wing-left',
    name: 'Left Wing',
    position: {
      left: '25%',
      top: '35%',
      width: '15%',
      height: '20%'
    },
    stats: {
      returned: '1.1 hits/sq.ft',
      interpretation: 'Critical system - hits often fatal'
    }
  },
  {
    id: 'wing-right',
    name: 'Right Wing',
    position: {
      left: '60%',
      top: '35%',
      width: '15%',
      height: '20%'
    },
    stats: {
      returned: '1.1 hits/sq.ft',
      interpretation: 'Critical system - hits often fatal'
    }
  },
  {
    id: 'engine',
    name: 'Engine',
    position: {
      left: '45%',
      top: '5%',
      width: '10%',
      height: '25%'
    },
    stats: {
      returned: '1.3 hits/sq.ft',
      interpretation: 'Vital area - direct hits rarely survived'
    }
  },
  {
    id: 'crew-compartment',
    name: 'Crew Compartment',
    position: {
      left: '42%',
      top: '32%',
      width: '16%',
      height: '25%'
    },
    stats: {
      returned: '1.3 hits/sq.ft',
      interpretation: 'Vital area - crew stations'
    }
  },
  {
    id: 'tailplane',
    name: 'Tailplane',
    position: {
      left: '40%',
      top: '95%',
      width: '20%',
      height: '10%'
    },
    stats: {
      returned: '1.7 hits/sq.ft',
      interpretation: 'Relatively resilient'
    }
  }
];

const SurvivorshipBiasSimulation: React.FC = () => {
  const [started, setStarted] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [showFeedback, setShowFeedback] = useState(false);
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [compareView, setCompareView] = useState<'returned' | 'lost'>('returned');
  const [sliderPosition, setSliderPosition] = useState(50);
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const [showIndividualView, setShowIndividualView] = useState(false);
  const [currentStage, setCurrentStage] = useState<'intro' | 'assessment' | 'analysis' | 'comparison' | 'history'>('intro');
  const simulationRef = React.useRef<HTMLDivElement>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const handleReset = () => {
    setSelectedAreas(new Set());
    setShowFeedback(false);
    setHoveredArea(null);
    setShowComparison(false);
    setCompareView('returned');
    setSliderPosition(50);
    setShowIndividualView(false);
    setCurrentStage('intro');
    
    // Scroll back to top of simulation
    simulationRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  const handleAreaClick = (area: string) => {
    if (!showComparison && showFeedback) {
      setSelectedAreas(prev => {
        const newAreas = new Set(prev);
        if (newAreas.has(area)) {
          newAreas.delete(area);
        } else if (newAreas.size < 3) {
          newAreas.add(area);
        }
        return newAreas;
      });
    }
  };

  const handleAreaHover = (area: string | null) => {
    setHoveredArea(area);
  };

  const checkSolution = () => {
    setShowComparison(true);
    setCurrentStage('analysis');
  };

  const isCorrectSolution = 
    selectedAreas.size === 2 &&
    selectedAreas.has('engine') && 
    selectedAreas.has('crew-compartment');

  const handleSliderMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const position = ((x - rect.left) / rect.width) * 100;
    setSliderPosition(Math.min(Math.max(position, 0), 100));
  };

  const handleSliderStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (e.type === 'touchstart') {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleSliderEnd);
    } else {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleSliderEnd);
    }
  };

  const handleSliderEnd = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleSliderEnd);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleSliderEnd);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.min(Math.max(position, 0), 100));
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault(); // This is now safe because we set passive: false
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = ((e.touches[0].clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.min(Math.max(position, 0), 100));
  };

  const handleImageClick = (image: string) => {
    if (window.innerWidth < 640) { // Only open lightbox on mobile
      setLightboxImage(image);
    }
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const renderLightbox = () => {
    if (!lightboxImage) return null;

    return (
      <Portal>
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="relative bg-white rounded-2xl overflow-hidden max-w-full max-h-[90vh] w-[90vw] aspect-[2/1]">
            <img
              src={lightboxImage}
              alt="Enlarged view"
              className="w-full h-full object-contain"
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
      </Portal>
    );
  };

  const renderComparisonSlider = () => (
    <div 
      ref={sliderRef}
      className="relative w-[90%] sm:w-full max-w-2xl aspect-[2/1] select-none mb-8 mx-auto"
    >
      {/* Base plane outline */}
      <img
        src={planeOutline}
        alt="Plane outline"
        className="absolute inset-0 w-full h-full object-contain object-center"
        loading="lazy"
        decoding="async"
      />
      
      {/* Lost aircraft (right side) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ left: `${sliderPosition}%`, width: `${100 - sliderPosition}%` }}
      >
        <div 
          className="absolute inset-0"
          style={{ width: `${100 * (100 / (100 - sliderPosition))}%`, left: `-${sliderPosition * (100 / (100 - sliderPosition))}%` }}
        >
          <img
            src={notReturnedPlane}
            alt="Lost aircraft damage pattern"
            className="w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute top-4 right-4 bg-orange-500/90 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
            Lost Aircraft
          </div>
        </div>
      </div>
      
      {/* Returned aircraft (left side) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <div 
          className="absolute inset-0"
          style={{ width: `${100 * (100 / sliderPosition)}%` }}
        >
          <img
            src={returnedPlane}
            alt="Returning aircraft damage pattern"
            className="w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute top-4 left-4 bg-orange-500/90 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
            Returned Aircraft
          </div>
        </div>
      </div>

      {/* Slider handle */}
      <div 
        className="absolute inset-y-0 w-1 bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)] cursor-col-resize group"
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={handleSliderStart}
        onTouchStart={handleSliderStart}
      >
        <div className="h-8 w-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 rounded-full shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform">
          <ArrowLeftRight className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );

  const renderMobileArmorSelection = () => {
    const maxAreasSelected = selectedAreas.size >= 3;
    
    return (
      <div className="block sm:hidden space-y-2">
        <p className="text-sm text-gray-600 mb-2">Select up to three areas to armor:</p>
        <div className="grid grid-cols-2 gap-2">
          {DAMAGE_AREAS.map(area => {
            const isSelected = selectedAreas.has(area.id);
            return (
              <OutlineButton
                key={area.id}
                onClick={() => !showComparison && handleAreaClick(area.id)}
                disabled={!isSelected && maxAreasSelected}
                active={isSelected}
                className="py-3 px-2 text-sm text-center min-h-[64px] flex items-center justify-center"
              >
                {area.name}
              </OutlineButton>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMobileComparison = () => (
    <div className="block sm:hidden w-full space-y-4">
      <div className="flex items-center justify-center gap-2">
        <OutlineButton
          onClick={() => setCompareView('returned')}
          active={compareView === 'returned'}
          className="px-3 py-2 text-sm"
        >
          Returned Aircraft
        </OutlineButton>
        <OutlineButton
          onClick={() => setCompareView('lost')}
          active={compareView === 'lost'}
          className="px-3 py-2 text-sm"
        >
          Lost Aircraft
        </OutlineButton>
      </div>
      <div className="w-full overflow-hidden py-12 pb-16">
        <div className="w-full max-w-2xl mx-auto transform scale-125">
          {renderPlane(compareView, 'comparison')}
        </div>
      </div>
    </div>
  );

  const renderPlane = (view: 'returned' | 'lost', section?: 'assessment' | 'analysis' | 'comparison') => {
    const baseImage = planeOutline;
    const overlayImage = view === 'returned' ? returnedPlane : notReturnedPlane;
    
    return (
      <div className="relative w-full aspect-[2/1] sm:cursor-default cursor-zoom-in"
        onClick={() => handleImageClick(overlayImage)}
      >
        <div className="absolute inset-0">
          {/* Base plane outline */}
          <img
            src={baseImage}
            alt="Plane outline"
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
          
          {/* Damage pattern overlay */}
          <img
            src={overlayImage}
            alt={`${view === 'returned' ? 'Returning' : 'Lost'} aircraft damage pattern`}
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
          
          {/* Clickable areas - show in Assessment section on desktop when showFeedback is true */}
          {section === 'assessment' && view === 'returned' && showFeedback && (
            <div className="absolute inset-0 hidden sm:block">
              {DAMAGE_AREAS.map(area => {
                const isSelected = selectedAreas.has(area.id);
                const isHovered = hoveredArea === area.id;
                const maxAreasSelected = selectedAreas.size >= 3;
                const canInteract = !showComparison && currentStage === 'assessment';
                
                return (
                  <div 
                    key={area.id}
                    onMouseEnter={() => canInteract && handleAreaHover(area.id)}
                    onMouseLeave={() => canInteract && handleAreaHover(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      canInteract && handleAreaClick(area.id);
                    }}
                    className={`absolute transition-all duration-300 border-2 group
                      ${canInteract && (!maxAreasSelected || isSelected) ? 'hover:border-orange-500 hover:bg-orange-500/10' : ''}
                      ${isSelected ? 'border-orange-500 bg-orange-500/20' : 'border-transparent'}
                      ${isHovered && (!maxAreasSelected || isSelected) && canInteract ? 'border-orange-300' : ''}
                      ${!canInteract && isSelected ? 'opacity-75' : ''}
                    `}
                    style={{
                      left: area.position.left,
                      top: area.position.top,
                      width: area.position.width,
                      height: area.position.height,
                      borderRadius: '4px',
                      cursor: canInteract ? (maxAreasSelected && !isSelected ? 'not-allowed' : 'pointer') : 'default'
                    }}
                  >
                    {/* Tooltip */}
                    <div className="absolute left-1/2 -translate-x-1/2 -top-8 hidden group-hover:block pointer-events-none">
                      <div className="bg-gray-900/90 text-white text-sm px-2 py-1 rounded whitespace-nowrap">
                        {area.name}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full h-full backdrop-blur-[1px] border-2 bg-orange-100/80 border-orange-400" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderIntroPlane = () => {
    return (
      <div className="relative w-full aspect-[2/1] sm:cursor-default cursor-zoom-in"
        onClick={() => handleImageClick(returnedPlane)}
      >
        <div className="absolute inset-0">
          {/* Base plane outline */}
          <img
            src={planeOutline}
            alt="Plane outline"
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
          
          {/* Damage pattern overlay */}
          <img
            src={returnedPlane}
            alt="Returning aircraft damage pattern"
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-2 sm:px-4">
      {!started ? (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-100">
          <Text className="text-gray-700 text-center mb-6">
            Experience a famous World War II study that revolutionized aircraft protection and revealed a crucial insight about data analysis. You'll examine damage patterns on bomber aircraft and discover how a counterintuitive solution saved many lives.
          </Text>
          <div className="flex justify-center">
            <AccentButton onClick={() => setStarted(true)}>
              Start Simulation
            </AccentButton>
          </div>
        </div>
      ) : (
        <div ref={simulationRef} className="max-w-4xl mx-auto p-0 sm:p-6">
          {renderLightbox()}
          <h1 className="text-xl font-semibold text-gray-900 text-center mb-4 sm:mb-8">
            Aircraft Damage Analysis: A Study in Survivorship Bias
          </h1>

          <div className="space-y-4 sm:space-y-8">
            {/* Step 1: Returning Aircraft */}
            <section className={`bg-white/90 backdrop-blur-sm rounded-3xl px-2 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8 shadow-lg border border-gray-100 transition-opacity duration-300 ${currentStage !== 'intro' ? 'opacity-50 pointer-events-none select-none' : ''}`}>
              <h2 className="text-xl font-semibold text-gray-900 mb-3 sm:mb-4">1. Damage Patterns on Returning Aircraft</h2>
              <p className="text-gray-600 mb-4 sm:mb-6">
                During World War II, the military documented bullet holes and damage on B-17 bombers that returned from missions.
                Study the damage patterns shown below and observe where the damage occurred most frequently.
              </p>
              <div className="flex flex-col items-center gap-8">
                <div className="w-full overflow-hidden py-12 pb-16">
                  <div className="w-full max-w-2xl mx-auto transform scale-125">
                    {renderIntroPlane()}
                  </div>
                </div>
                <AccentButton
                  onClick={() => {
                    setShowFeedback(true);
                    setCurrentStage('assessment');
                  }}
                  className="px-4 sm:px-6 py-2.5 sm:py-3"
                >
                  Next Step
                </AccentButton>
              </div>
            </section>

            {/* Step 2: Initial Assessment */}
            {showFeedback && (
              <section className={`bg-white/90 backdrop-blur-sm rounded-3xl px-2 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8 shadow-lg border border-gray-100 transition-opacity duration-300 ${currentStage !== 'assessment' ? 'opacity-50 pointer-events-none select-none' : ''}`}>
                <h2 className="text-xl font-semibold text-gray-900 mb-3 sm:mb-4">2. Your Assessment</h2>
                <p className="text-gray-600 mb-4 sm:mb-6">
                  Based on this data, select one to three areas where additional armor would be most effective in protecting the aircraft.
                  {window.innerWidth < 640 ? ' Tap the areas below to make your selection.' : ' Click on the areas you think need reinforcement.'}
                </p>
                <div className="flex flex-col items-center gap-8">
                  <div className="w-full overflow-hidden py-12 pb-16">
                    <div className="w-full max-w-2xl mx-auto transform scale-125">
                      {renderPlane('returned', 'assessment')}
                    </div>
                  </div>
                  {renderMobileArmorSelection()}
                  
                  <AccentButton
                    onClick={() => {
                      checkSolution();
                    }}
                    disabled={selectedAreas.size === 0 || selectedAreas.size > 3}
                    className="px-4 sm:px-6 py-2.5 sm:py-3"
                  >
                    Submit Analysis
                  </AccentButton>
                </div>
              </section>
            )}

            {/* Step 3: Analysis and Missing Data */}
            {showComparison && (
              <section className="bg-white/90 backdrop-blur-sm rounded-3xl px-4 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8 shadow-lg border border-gray-100 mt-16 sm:mt-32">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">3. Analysis and Missing Data</h2>
                <div className="space-y-6 sm:space-y-8">
                  <div>
                    <p className="text-gray-600 mb-4 sm:mb-6">
                      Now, let's look at the likely damage patterns on aircraft that never returned.
                      These represent the planes that sustained fatal damage.
                    </p>
                    <div className="flex flex-col items-center gap-8">
                      <div className="w-full overflow-hidden py-12 pb-16">
                        <div className="w-full max-w-2xl mx-auto transform scale-125">
                          {renderPlane('lost', 'analysis')}
                        </div>
                      </div>

                      <div className={`w-full p-3 sm:p-4 rounded-xl ${isCorrectSolution ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                        {isCorrectSolution ? (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <ShieldCheck className="w-5 sm:w-6 h-5 sm:h-6" />
                              <p className="font-medium">Excellent analysis!</p>
                            </div>
                            <p className="text-sm">
                              You've correctly identified that the areas showing less damage on returning aircraft
                              were actually the most critical. Hits to the engines and cockpit were often fatal,
                              which is why we rarely saw damage there on returning planes.
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-5 sm:w-6 h-5 sm:h-6" />
                              <p className="font-medium">Consider the missing data</p>
                            </div>
                            <p className="text-sm">
                              The areas showing the most damage on returning aircraft were actually the least critical.
                              The absence of damage in certain areas (engines and cockpit) suggests that hits there
                              were usually fatal - these planes didn't make it back to be studied.
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Step 4: Comparison - Modified for mobile */}
            {showComparison && (
              <section className="bg-white/90 backdrop-blur-sm rounded-3xl px-2 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8 shadow-lg border border-gray-100 mt-16 sm:mt-32">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">4. Compare Both Scenarios</h2>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex flex-col gap-4 sm:gap-6">
                    <p className="text-gray-600">
                      {window.innerWidth < 640 
                        ? 'Toggle between views to compare damage patterns:'
                        : 'Drag the slider to compare damage patterns between returning and lost aircraft:'}
                    </p>
                    <div className="hidden sm:block">
                      {renderComparisonSlider()}
                    </div>
                    {renderMobileComparison()}
                  </div>
                </div>
              </section>
            )}

            {/* Step 5: Historical Context */}
            {showComparison && (
              <section className="bg-white/90 backdrop-blur-sm rounded-3xl px-4 sm:px-8 pt-3 sm:pt-4 pb-6 sm:pb-8 shadow-lg border border-gray-100 mt-12 sm:mt-24">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">5. Results</h2>
                <div className="space-y-6">
                  <ResultBlock
                    title="Understanding Your Results"
                    icon={Brain}
                    variant="highlight"
                  >
                    <div className="space-y-4">
                      <Text className="text-gray-700">
                        You identified areas that needed additional armor based on where you saw the most damage on returning aircraft. This intuitive approach actually demonstrates survivorship bias - we're only seeing the planes that made it back.
                      </Text>
                      <Text className="text-gray-700">
                        The areas with fewer visible hits on returning planes are actually the most critical - these were the areas where hits were often fatal, preventing those planes from returning. The high concentration of damage on wings and tail suggests these areas could sustain significant damage while still allowing the plane to return.
                      </Text>
                    </div>
                  </ResultBlock>

                  <ResultBlock
                    title="In the Original Study"
                    icon={Microscope}
                    variant="info"
                  >
                    <div className="space-y-4">
                      <Text className="text-gray-700">
                        In 1943, statistician Abraham Wald made a crucial observation while analyzing damage patterns on returning WWII aircraft. While military officers wanted to reinforce areas showing the most damage, Wald realized this was exactly wrong.
                      </Text>
                      <Text className="text-gray-700">
                        His key insight: The damage patterns on returning aircraft actually showed where planes could be hit and still survive. The areas showing little or no damage were likely the most vulnerable - hits to these areas prevented planes from returning at all.
                      </Text>
                      <Text className="text-gray-700">
                        This led to a complete reversal of the military's armor strategy, focusing protection on areas showing the least damage in the surviving planes - particularly around the engines and cockpit.
                      </Text>
                    </div>
                  </ResultBlock>

                  <ResultBlock
                    title="About the Study"
                    icon={BookOpen}
                    variant="muted"
                  >
                    <div className="space-y-4">
                      <Text className="text-gray-700">
                        Based on Abraham Wald's 1943 work with the Statistical Research Group during World War II, documented in "A Method of Estimating Plane Vulnerability Based on Damage of Survivors." This groundbreaking analysis demonstrated how survivorship bias can lead to dangerously wrong conclusions when we only consider successful cases.
                      </Text>
                      <Text className="text-sm text-gray-500 italic">
                        This simulation is designed for educational purposes. Individual results may vary.
                      </Text>
                    </div>
                  </ResultBlock>

                  <div className="flex justify-center">
                    <AccentButton onClick={handleReset}>
                      Try Again
                    </AccentButton>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurvivorshipBiasSimulation; 